/**
 * Leading + trailing throttle.
 *
 * Calls `fn` immediately on the first call, then suppresses subsequent calls
 * within `waitMs` ms and fires one trailing call with the latest args at the
 * end of the window. Useful for write-heavy persistence (e.g. workspace
 * autosave) where the first event should commit eagerly but bursts should
 * coalesce.
 */

export type ThrottleHandle = {
  cancel: () => void;
  flush: () => void;
};

export function throttleLeadingTrailing<A extends readonly unknown[]>(
  fn: (...args: A) => void,
  waitMs: number,
): ((...args: A) => void) & ThrottleHandle {
  let lastArgs: A | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastInvoke = 0;

  const invoke = (args: A) => {
    lastInvoke = Date.now();
    lastArgs = null;
    fn(...args);
  };

  const throttled = ((...args: A) => {
    const now = Date.now();
    const sinceLast = now - lastInvoke;
    if (sinceLast >= waitMs) {
      // Leading edge.
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      invoke(args);
      return;
    }
    // Within window: queue trailing call.
    lastArgs = args;
    if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        if (lastArgs) invoke(lastArgs);
      }, waitMs - sinceLast);
    }
  }) as ((...args: A) => void) & ThrottleHandle;

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
  };

  throttled.flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (lastArgs) invoke(lastArgs);
  };

  return throttled;
}
